/**
 * Create Automation Wizard
 *
 * Multi-step dialog for creating new automations.
 * Steps:
 * 1. Select Action - What the automation does
 * 2. Select Trigger - When it fires (skipped for recurring actions)
 * 3. Name - Required name for the automation
 */

import { faSearch } from '@fortawesome/pro-regular-svg-icons';
// eslint-disable-next-line no-restricted-imports
import { Step, StepButton, Stepper } from '@mui/material';
import { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Chip } from 'src/components/Chip';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { ButtonPropsWithLabel } from 'src/components/Dialog';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { InputAdornment } from 'src/components/InputAdornment';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { AutomationGroup } from 'src/models/automation-group/_types';
import {
  AUTOMATION_ACTIONS,
  getAutomationAction,
  getAutomationActionLabel,
} from 'src/shared/Automation/automationActions';
import { getTriggerOptionsFromIds } from 'src/shared/Automation/automationTriggers';
import { ActionTriggerCard } from 'src/shared/Automation/components';
import { Identifier } from 'src/types/utilityTypes';

import { getMaxReachableStepIndex } from './createAutomationWizardStepNavigation';


export interface CreateAutomationWizardProps {
  folders?: AutomationGroup[];
  open: boolean;
  onClose: () => void;
  onAdd: (data: {
    automationGroupId?: Identifier | null;
    customType: string;
    reminderType: string;
    name: string;
  }) => void;
}

export const CreateAutomationWizard: FC<CreateAutomationWizardProps> = ({
  folders = [],
  open,
  onClose,
  onAdd,
}) => {
  const { t } = useTranslation();

  const [closeConfirmProps, confirmClose] = useConfirmDialogComponent();

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [selectedTrigger, setSelectedTrigger] = useState<string | null>(null);
  const [automationName, setAutomationName] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<Identifier | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const actionCardRefs = useRef<Map<string, HTMLElement>>(new Map());
  const triggerCardRefs = useRef<Map<string, HTMLElement>>(new Map());

  const setActionCardRef = (value: string) => (el: HTMLElement | null) => {
    const map = actionCardRefs.current;
    if (el) {
      map.set(value, el);
    } else {
      map.delete(value);
    }
  };

  const setTriggerCardRef = (value: string) => (el: HTMLElement | null) => {
    const map = triggerCardRefs.current;
    if (el) {
      map.set(value, el);
    } else {
      map.delete(value);
    }
  };

  // Get action config for selected action
  const actionConfig = selectedAction
    ? getAutomationAction(selectedAction)
    : null;

  // Check if this is a recurring action (no triggers)
  const isRecurring = actionConfig?.triggers.length === 0;

  // Build available actions list
  const actionOptions = useMemo(() => {
    const options = Object.entries(AUTOMATION_ACTIONS)
      .map(([key, config]) => ({
        value: key,
        label: getAutomationActionLabel(key, t),
        hasTriggers: config.triggers.length > 0,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    if (!searchQuery.trim()) return options;

    const query = searchQuery.toLowerCase();
    return options.filter(
      (action) =>
        action.label.toLowerCase().includes(query) ||
        t(`automations.actionDescriptions.${action.value}`, '')
          .toLowerCase()
          .includes(query)
    );
  }, [searchQuery, t]);

  // Build trigger options for selected action
  const triggerOptions = useMemo(() => {
    if (!actionConfig) return [];
    const options = getTriggerOptionsFromIds(actionConfig.triggers);

    if (!searchQuery.trim()) return options;

    const query = searchQuery.toLowerCase();
    return options.filter(
      (trigger) =>
        trigger.label.toLowerCase().includes(query) ||
        t(`automations.triggerDescriptions.${trigger.value}`, '')
          .toLowerCase()
          .includes(query)
    );
  }, [actionConfig, searchQuery, t]);

  // Determine steps based on whether action has triggers
  const steps = useMemo(() => {
    const baseSteps = [
      { key: 'action', label: t('automations.wizard.steps.action') },
    ];

    // Only include trigger step if action has triggers
    if (!selectedAction || !isRecurring) {
      baseSteps.push({
        key: 'trigger',
        label: t('automations.wizard.steps.trigger'),
      });
    }

    baseSteps.push({ key: 'name', label: t('automations.wizard.steps.name') });

    return baseSteps;
  }, [selectedAction, isRecurring, t]);

  // Get current step key
  const currentStepKey = steps[activeStep]?.key;

  const maxReachableStepIndex = getMaxReachableStepIndex({
    selectedAction,
    isRecurring,
    selectedTrigger,
  });

  // When navigating back to action/trigger, scroll the selected card into view inside the scrollable grid
  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    if (currentStepKey === 'action' && selectedAction) {
      actionCardRefs.current
        .get(selectedAction)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } else if (currentStepKey === 'trigger' && selectedTrigger) {
      triggerCardRefs.current
        .get(selectedTrigger)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [open, currentStepKey, selectedAction, selectedTrigger]);

  // Reset wizard state
  const resetWizard = () => {
    setActiveStep(0);
    setSelectedAction(null);
    setSelectedTrigger(null);
    setAutomationName('');
    setSelectedFolderId(null);
    setSearchQuery('');
  };

  // Handle close (after confirm when there is progress)
  const handleClose = () => {
    resetWizard();
    onClose();
  };

  const hasWizardProgress = () =>
    activeStep > 0 ||
    selectedAction !== null ||
    selectedTrigger !== null ||
    automationName.trim() !== '' ||
    searchQuery.trim() !== '';

  const requestClose = () => {
    void (async () => {
      if (!hasWizardProgress()) {
        handleClose();
        return;
      }
      const ok = await confirmClose({
        title: t('automations.wizard.closeConfirmTitle'),
        message: t('automations.wizard.closeConfirmMessage'),
        cancelText: t('common.actions.cancel'),
        confirmColor: 'error',
        confirmText: t('common.buttons.leaveWithoutSaving'),
      });
      if (ok) {
        handleClose();
      }
    })();
  };

  // Handle action selection
  const handleActionSelect = (actionKey: string) => {
    if (actionKey !== selectedAction) {
      setSelectedTrigger(null);
    }
    setSelectedAction(actionKey);
    setSearchQuery(''); // Reset search for next step

    const config = getAutomationAction(actionKey);
    // If recurring (no triggers), skip trigger step
    if (config?.triggers.length === 0) {
      setActiveStep(1); // Go directly to name step (which is index 1 when trigger is skipped)
    } else {
      setActiveStep(1); // Go to trigger step
    }
  };

  // Handle trigger selection
  const handleTriggerSelect = (triggerKey: string) => {
    setSelectedTrigger(triggerKey);
    setSearchQuery(''); // Reset search for next step
    setActiveStep(activeStep + 1); // Go to name step
  };

  // Handle back
  const handleBack = () => {
    if (currentStepKey === 'name' && isRecurring) {
      // If recurring, go back to action step
      setActiveStep(0);
    } else {
      setActiveStep(activeStep - 1);
    }
  };

  // Jump to another step from the stepper when that step is reachable
  const handleStepClick = (index: number) => {
    if (index === activeStep || index > maxReachableStepIndex) {
      return;
    }
    setActiveStep(index);
  };

  // Handle add
  const handleAdd = () => {
    if (!selectedAction) return;

    onAdd({
      automationGroupId: selectedFolderId,
      customType: selectedAction,
      reminderType: selectedTrigger || '',
      name: automationName.trim(),
    });

    resetWizard();
  };

  // Check if next/create is enabled
  const canProceed = () => {
    switch (currentStepKey) {
      case 'action':
        return !!selectedAction;
      case 'trigger':
        return !!selectedTrigger;
      case 'name':
        return automationName.trim().length > 0;
      default:
        return false;
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStepKey) {
      case 'action':
        return (
          <Stack spacing={2}>
            <Typography variant="h6">
              {t('automations.wizard.selectActionTitle')}
            </Typography>
            <TextField
              placeholder={t('common.actions.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon={faSearch} />
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 2,
                maxHeight: 400,
                overflowY: 'auto',
                pb: 2,
              }}
            >
              {actionOptions.map((action) => (
                <Box
                  key={action.value}
                  ref={setActionCardRef(action.value)}
                  sx={{ minWidth: 0 }}
                >
                  <ActionTriggerCard
                    title={action.label}
                    description={t(`automations.actionDescriptions.${action.value}`, '')}
                    selected={action.value === selectedAction}
                    onClick={() => handleActionSelect(action.value)}
                  />
                </Box>
              ))}
            </Box>
          </Stack>
        );

      case 'trigger':
        return (
          <Stack spacing={2}>
            <Typography variant="h6">
              {t('automations.wizard.selectTriggerTitle')}
            </Typography>
            <TextField
              placeholder={t('common.actions.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon={faSearch} />
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 2,
                maxHeight: 400,
                overflowY: 'auto',
                pb: 2,
              }}
            >
              {triggerOptions.map((trigger) => (
                <Box
                  key={trigger.value}
                  ref={setTriggerCardRef(trigger.value)}
                  sx={{ minWidth: 0 }}
                >
                  <ActionTriggerCard
                    title={trigger.label}
                    description={t(`automations.triggerDescriptions.${trigger.value}`, '')}
                    selected={trigger.value === selectedTrigger}
                    onClick={() => handleTriggerSelect(trigger.value)}
                  />
                </Box>
              ))}
            </Box>
          </Stack>
        );

      case 'name': {
        const selectedFolder =
          folders.find((folder) => String(folder.id) === String(selectedFolderId)) ??
          null;

        return (
          <Stack spacing={3}>
            <Typography variant="h6">
              {t('automations.wizard.nameTitle')}
            </Typography>

            <TextField
              autoFocus
              fullWidth
              label={t('automations.wizard.nameLabel')}
              onChange={(e) => setAutomationName(e.target.value)}
              placeholder={t('automations.wizard.namePlaceholder')}
              required
              value={automationName}
            />

            {folders.length > 0 ? (
              <Autocomplete<AutomationGroup>
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                onChange={(_, value) => setSelectedFolderId(value?.id ?? null)}
                options={folders}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('automations.wizard.folderLabel')}
                    placeholder={t('automations.wizard.folderPlaceholder')}
                  />
                )}
                value={selectedFolder}
              />
            ) : null}
          </Stack>
        );
      }

      default:
        return null;
    }
  };

  // Build dialog actions
  const dialogActions: ButtonPropsWithLabel[] = [];

  // Back button (not on first step)
  if (activeStep > 0) {
    dialogActions.push({
      onClick: handleBack,
      label: t('common.back'),
    });
  }

  // Add button
  if (currentStepKey === 'name') {
    dialogActions.push({
      onClick: handleAdd,
      label: t('automations.wizard.addButton'),
      variant: 'prominent',
      color: 'primary',
      disabled: !canProceed(),
    });
  }

  // Render a summary chip strip for the action (and trigger on name step) so
  // the user keeps context after leaving the action step.
  const renderSelectionContextStrip = () => {
    if (currentStepKey === 'action' || !actionConfig) {
      return null;
    }

    const triggerConfigForStrip = triggerOptions.find(
      (opt) => opt.value === selectedTrigger
    );
    const triggerLabel =
      triggerConfigForStrip?.label || selectedTrigger || '';
    const showTrigger =
      currentStepKey === 'name' && !isRecurring && !!triggerLabel;

    return (
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('automations.wizard.actionLabel')}:
        </Typography>
        <Chip
          label={actionConfig.label}
          size="small"
          variant="outlined"
          sx={{ textTransform: 'capitalize' }}
        />
        <Button
          size="small"
          variant="borderless"
          onClick={() => setActiveStep(0)}
        >
          {t('automations.wizard.change')}
        </Button>
        {showTrigger && (
          <>
            <Typography variant="body2" color="text.secondary">
              {t('automations.wizard.triggerLabel')}:
            </Typography>
            <Chip label={triggerLabel} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
            <Button
              size="small"
              variant="borderless"
              onClick={() => {
                const triggerStepIndex = steps.findIndex(
                  (s) => s.key === 'trigger'
                );
                if (triggerStepIndex >= 0) {
                  setActiveStep(triggerStepIndex);
                }
              }}
            >
              {t('automations.wizard.change')}
            </Button>
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        title={t('automations.wizard.title')}
        maxWidth="md"
        fullWidth
        actions={dialogActions}
      >
        <Box sx={{ width: '100%', mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel nonLinear>
            {steps.map((step, index) => (
              <Step key={step.key} completed={index < activeStep}>
                <StepButton
                  color="inherit"
                  disabled={
                    index === activeStep || index > maxReachableStepIndex
                  }
                  onClick={() => handleStepClick(index)}
                  aria-label={
                    index !== activeStep && index <= maxReachableStepIndex
                      ? t('automations.wizard.goToStep', { step: step.label })
                      : undefined
                  }
                >
                  {step.label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ minHeight: 520, display: 'flex', flexDirection: 'column' }}>
          {renderSelectionContextStrip()}
          {renderStepContent()}
        </Box>
      </Dialog>
      <ConfirmDialog {...closeConfirmProps} />
    </>
  );
};
